import { useCallback, type PropsWithChildren } from "react";

import DOMPurify from "dompurify";

import { Trans, useTranslate } from "@similarweb/i18n-provider";

import { useActivationSet } from "../../hooks/useActivationSet";
import { useActivationSetFavicons } from "../../hooks/useActivationSetFavicons";
import { AlertsFeed } from "../AlertsFeed";
import { Virtualized } from "../Virtualized";

import { KeywordAlert } from "./components/KeywordAlert";
import { LandingPageAlert } from "./components/LandingPageAlert";
import type { SeoAlertItem } from "./useSeoAlerts";
import { useSeoAlerts } from "./useSeoAlerts";

// Used by Trans component to strip <b> tags while preserving content
// eslint-disable-next-line react/jsx-no-useless-fragment
const ChildrenOnly = ({ children }: PropsWithChildren) => <>{children}</>;

interface ISeoAlertCardProps {
    alert: SeoAlertItem;
    country: number | undefined;
    favicon: string;
}

const SeoAlertCard = ({ alert, country, favicon }: ISeoAlertCardProps) => {
    const { translate } = useTranslate();
    const { alertType, domain, insight, topNewItems, totalNewItemsNumber, trafficType, formattedDuration } = alert;

    const trafficTypeKey = trafficType === "Paid" ? "paid" : "organic";
    const alertTypeKey = alertType === "NewPages" ? "new-landing-pages" : "new-keywords";

    const title = (
        <Trans
            i18nKey={`activation.alerts.short.${alertTypeKey}.${trafficTypeKey}`}
            values={{ domain, amount: totalNewItemsNumber }}
            components={{ b: <ChildrenOnly /> }}
            t={translate}
        />
    );

    const insightText = (
        // eslint-disable-next-line react/no-danger -- API HTML content sanitized with DOMPurify
        <div
            // eslint-disable-next-line @typescript-eslint/naming-convention -- __html is required by React's dangerouslySetInnerHTML
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(insight) }}
        />
    );

    const commonProps = {
        date: formattedDuration,
        domain,
        country,
        iconUrl: favicon,
        text: insightText,
        title,
        trafficType,
    };

    if (alertType === "NewKeywords") {
        return (
            <KeywordAlert
                {...commonProps}
                keywords={topNewItems}
            />
        );
    }

    if (alertType === "NewPages") {
        return (
            <LandingPageAlert
                {...commonProps}
                landingPages={topNewItems}
            />
        );
    }

    return null;
};

export const SeoAlertsFeed = () => {
    const { data = [], isLoading: isAlertsLoading } = useSeoAlerts();
    const { activationSet, isLoading: isActivationSetLoading } = useActivationSet();
    const { favicons, isLoading: isFaviconsLoading } = useActivationSetFavicons();

    const getItemKey = useCallback((index: number) => data[index].id, [data]);

    return (
        <AlertsFeed
            isLoading={isAlertsLoading || isActivationSetLoading || isFaviconsLoading}
            isEmpty={!data.length}
        >
            <Virtualized
                count={data.length}
                getItemKey={getItemKey}
                renderItem={(index) => (
                    <SeoAlertCard
                        alert={data[index]}
                        country={activationSet?.country}
                        favicon={favicons?.[data[index].domain] ?? ""}
                    />
                )}
            />
        </AlertsFeed>
    );
};
